(() => {
  const openTpuShopifyChat = () => {
    let attempts = 0;

    const chatRoots = (host) => {
      const roots = [];
      if (host.shadowRoot) roots.push(host.shadowRoot);
      const nested = host.shadowRoot && host.shadowRoot.querySelector(
        'shopify-agent-runtime, inbox-online-store-chat'
      );
      if (nested && nested.shadowRoot) roots.push(nested.shadowRoot);
      return roots;
    };

    const findLauncher = (roots) => {
      for (let i = 0; i < roots.length; i += 1) {
        const root = roots[i];
        const launcher =
          root.querySelector('button.activator') ||
          root.querySelector('button.chat-toggle') ||
          root.querySelector('.chat-toggle') ||
          root.querySelector('button[aria-controls="chat-ui"]') ||
          root.querySelector('.chat-app > button') ||
          root.querySelector('button[aria-label="Chat"]');
        if (launcher) return launcher;
      }
      return null;
    };

    const isChatOpen = (host, launcher) => {
      if (host.getAttribute('is-open') === 'true' || host.hasAttribute('open')) return true;
      const runtime = host.shadowRoot && host.shadowRoot.querySelector('shopify-agent-runtime');
      if (runtime && runtime.hasAttribute('open')) return true;
      return !!(launcher && launcher.getAttribute('aria-expanded') === 'true');
    };

    const tryOpen = () => {
      const host =
        document.querySelector('shopify-chat') ||
        document.getElementById('ShopifyChat') ||
        document.querySelector('inbox-online-store-chat');
      if (!host) {
        if (attempts >= 20) return;
        attempts += 1;
        window.setTimeout(tryOpen, 200);
        return;
      }

      const roots = chatRoots(host);
      if (!roots.length) {
        if (attempts >= 20) return;
        attempts += 1;
        window.setTimeout(tryOpen, 200);
        return;
      }

      const launcher = findLauncher(roots);
      if (isChatOpen(host, launcher)) return;

      if (launcher) {
        // Nested .click() inside the Contact Us handler is ignored after first open.
        window.setTimeout(() => {
          if (isChatOpen(host, launcher)) return;
          launcher.click();
        }, 0);
        return;
      }

      if (attempts >= 20) return;
      attempts += 1;
      window.setTimeout(tryOpen, 200);
    };
    tryOpen();
  };

  window.__tpuOpenShopifyChat = openTpuShopifyChat;

  if (!window.__tpuChatClickBound) {
    window.__tpuChatClickBound = true;
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-tpu-chat]');
      if (!trigger) return;
      event.preventDefault();
      const drawerInput = document.getElementById('mobile-drawer-input');
      if (drawerInput) drawerInput.checked = false;
      document.body.classList.remove('tpu-mobile-drawer-open');
      document.querySelectorAll('[data-tpu-header]').forEach((header) => {
        header.classList.remove('is-drawer-open');
      });
      window.__tpuOpenShopifyChat();
    });
  }

  if (window.__tpuHeaderJsLoaded) return;
  window.__tpuHeaderJsLoaded = true;

  const initialized = new WeakSet();
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const canStore = () => {
    try {
      const key = 'tpu-storage-test';
      window.localStorage.setItem(key, key);
      window.localStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  };

  const storageAvailable = canStore();
  const mobileHeaderQuery = window.matchMedia('(max-width: 991px)');
  const utilityMoreDesktopQuery = window.matchMedia('(min-width: 1024px)');
  const utilityMoreInitialized = new WeakSet();
  let utilityMoreDocListenersBound = false;

  // Shared document-level close handlers, bound once for all "More" menus
  // (mirrors the [data-tpu-chat] delegate pattern) so theme-editor section
  // re-renders never accumulate listeners.
  const closeUtilityMore = (openLi, refocus) => {
    openLi.classList.remove('is-open');
    const openToggle = openLi.querySelector('.tpu-header__more-toggle');
    if (openToggle) {
      openToggle.setAttribute('aria-expanded', 'false');
      if (refocus) openToggle.focus();
    }
    const openPanel = openLi.querySelector('.tpu-header__more-list');
    if (openPanel) openPanel.setAttribute('aria-hidden', 'true');
  };

  const bindUtilityMoreDocListeners = () => {
    if (utilityMoreDocListenersBound) return;
    utilityMoreDocListenersBound = true;
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-tpu-more].is-open').forEach((openLi) => {
        closeUtilityMore(openLi, true);
      });
    });
    document.addEventListener('click', (event) => {
      document.querySelectorAll('[data-tpu-more].is-open').forEach((openLi) => {
        if (!openLi.contains(event.target)) closeUtilityMore(openLi, false);
      });
    });
  };

  // Desktop utility-nav "More" overflow menu (TT05-04): moves nav links that
  // do not fit into a trailing "More" dropdown and back on resize.
  const initUtilityMore = (header) => {
    const list = header.querySelector('.tpu-header__utility-list');
    const moreLi = list && list.querySelector('[data-tpu-more]');
    const toggle = moreLi && moreLi.querySelector('.tpu-header__more-toggle');
    const moreList = moreLi && moreLi.querySelector('.tpu-header__more-list');
    if (!list || !moreLi || !toggle || !moreList) return;
    if (utilityMoreInitialized.has(list)) return;
    utilityMoreInitialized.add(list);

    // Content items in their original DOM order; the More <li> is excluded.
    const contentLis = Array.from(list.children).filter((li) => li !== moreLi);

    // Re-insert every content item before the More <li> in original order.
    // Always relocate (not just missing ones) so order stays exact no matter
    // how many items are currently inside the dropdown.
    const restoreAll = () => {
      contentLis.forEach((li) => list.insertBefore(li, moreLi));
    };

    const setOpen = (open) => {
      moreLi.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      moreList.setAttribute('aria-hidden', open ? 'false' : 'true');
    };
    const isOpen = () => moreLi.classList.contains('is-open');

    let hoverTimer = null;
    const cancelClose = () => {
      if (hoverTimer) {
        window.clearTimeout(hoverTimer);
        hoverTimer = null;
      }
    };
    const scheduleClose = () => {
      cancelClose();
      hoverTimer = window.setTimeout(() => setOpen(false), 150);
    };

    const measure = () => {
      // Section may have been re-rendered by the theme editor; skip dead work.
      if (!document.contains(header)) return;

      restoreAll();
      if (!utilityMoreDesktopQuery.matches) {
        moreLi.classList.add('is-hidden');
        setOpen(false);
        return;
      }

      // Reveal the More item first so its own width is reserved during
      // measurement (do NOT use scrollWidth/clientWidth here — with
      // overflow: visible that check is engine-dependent).
      moreLi.classList.remove('is-hidden');

      // Direction-aware geometry: in RTL the flex overflow spills to the
      // left, so compare left edges instead of right edges.
      // Mid screens (< 1441px) reserve an extra 100px so the trailing
      // "More" item — and its right-aligned dropdown — never crowd the
      // viewport edge.
      const midScreenAllowance = window.innerWidth < 1441 ? 100 : 0;
      const isRtl = window.getComputedStyle(list).direction === 'rtl';
      const listRect = list.getBoundingClientRect();
      const listEdge = isRtl
        ? listRect.left + 1 + midScreenAllowance
        : listRect.right - 1 - midScreenAllowance;
      const lastContent = () => moreLi.previousElementSibling;
      const overflows = () => {
        const last = lastContent();
        if (!last) return false;
        const rect = last.getBoundingClientRect();
        return isRtl ? rect.left < listEdge : rect.right > listEdge;
      };

      // Move trailing items into the dropdown while they overflow. The guard
      // terminates even when a single item is wider than the whole row.
      let guard = contentLis.length;
      while (overflows() && guard-- > 0) {
        moreList.appendChild(lastContent());
      }

      if (!moreList.childElementCount) {
        moreLi.classList.add('is-hidden');
      }
    };

    let measurePending = false;
    const requestMeasure = () => {
      if (measurePending) return;
      measurePending = true;
      requestAnimationFrame(() => {
        measurePending = false;
        measure();
      });
    };

    window.addEventListener('resize', requestMeasure);
    if (typeof utilityMoreDesktopQuery.addEventListener === 'function') {
      utilityMoreDesktopQuery.addEventListener('change', requestMeasure);
    } else if (typeof utilityMoreDesktopQuery.addListener === 'function') {
      utilityMoreDesktopQuery.addListener(requestMeasure);
    }
    if (document.fonts && typeof document.fonts.ready === 'object' && document.fonts.ready.then) {
      document.fonts.ready.then(requestMeasure).catch(() => {});
    }

    moreLi.addEventListener('mouseenter', () => {
      cancelClose();
      setOpen(true);
    });
    moreLi.addEventListener('mouseleave', scheduleClose);

    toggle.addEventListener('click', () => {
      setOpen(!isOpen());
    });

    bindUtilityMoreDocListeners();

    measure();
  };

  const publishHeaderHeights = () => {
    const headerGroup = document.querySelector('.header-group');
    const tpuHeader = document.querySelector('[data-tpu-header]');

    const groupHeight = headerGroup ? headerGroup.offsetHeight : 0;
    const barHeight = tpuHeader ? tpuHeader.offsetHeight : 0;

    if (groupHeight > 0) {
      document.documentElement.style.setProperty('--header-height', `${groupHeight}px`);
    }

    // Sticky bar only — announcement scrolls away; don't include it in offset.
    const stickyTop = barHeight || 52;

    document.documentElement.style.setProperty('--tpu-sticky-header-height', `${stickyTop}px`);
    document.dispatchEvent(new CustomEvent('header-height-change', { detail: `${stickyTop}px` }));
  };

  const initAnnouncementBar = (bar) => {
    if (!bar || initialized.has(bar)) return;
    initialized.add(bar);

    const items = Array.from(bar.querySelectorAll('[data-tpu-announcement-item]'));
    const dismiss = bar.querySelector('[data-tpu-announcement-dismiss]');
    const storageKey = bar.dataset.storageKey;

    if (storageAvailable && storageKey && window.localStorage.getItem(storageKey) === 'true') {
      bar.hidden = true;
      return;
    }

    if (dismiss) {
      dismiss.addEventListener('click', () => {
        bar.hidden = true;
        if (storageAvailable && storageKey) {
          window.localStorage.setItem(storageKey, 'true');
        }
      });
    }

    if (bar.dataset.display === 'marquee') return;
    if (items.length < 2) return;

    let activeIndex = Math.max(items.findIndex((item) => item.classList.contains('is-active')), 0);
    let timer = null;
    const interval = Math.max(parseInt(bar.dataset.interval, 10) || 4000, 2000);
    const autoplayEnabled = bar.dataset.autoplay === 'true' && !motionQuery.matches;

    const setActive = (index) => {
      activeIndex = index % items.length;
      items.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === activeIndex);
      });
    };

    const stop = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      if (!autoplayEnabled || timer || document.hidden) return;
      timer = window.setInterval(() => setActive(activeIndex + 1), interval);
    };

    bar.addEventListener('mouseenter', stop);
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', stop);
    bar.addEventListener('focusout', start);
    let touchStartX = null;
    bar.addEventListener('touchstart', (event) => {
      touchStartX = event.touches[0].clientX;
      stop();
    }, { passive: true });
    bar.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const deltaX = event.changedTouches[0].clientX - touchStartX;
      touchStartX = null;
      if (Math.abs(deltaX) > 40) {
        setActive(activeIndex + (deltaX < 0 ? 1 : items.length - 1));
      }
      start();
    }, { passive: true });

    if (!autoplayEnabled) return;

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    });

    start();
  };

  const initHeader = (header) => {
    if (!header || initialized.has(header)) return;
    initialized.add(header);

    // Portal drawer to body so sticky-header transforms (real iOS) cannot
    // turn position:fixed into a header-sized containing block.
    const mobileMenu = header.querySelector('.tpu-header__mobile-menu');
    const drawerInput = mobileMenu && mobileMenu.querySelector('#mobile-drawer-input');
    if (mobileMenu && mobileMenu.parentElement !== document.body) {
      document.body.appendChild(mobileMenu);
    }
    if (drawerInput) {
      const syncDrawerOpenState = () => {
        const open = drawerInput.checked;
        header.classList.toggle('is-drawer-open', open);
        document.body.classList.toggle('tpu-mobile-drawer-open', open);
        if (open && typeof header.getAnimations === 'function') {
          header.getAnimations().forEach((animation) => animation.cancel());
        }
      };
      drawerInput.addEventListener('change', syncDrawerOpenState);
      syncDrawerOpenState();
    }

    const searchForm = header.querySelector('[data-tpu-search-form]');
    if (searchForm) {
      searchForm.addEventListener('submit', (event) => {
        const category = searchForm.querySelector('[data-tpu-search-category]');
        const input = searchForm.querySelector('input[name="q"]');
        const term = input ? input.value.trim() : '';
        if (category && category.value && !term) {
          event.preventDefault();
          window.location.href = category.value;
        }
      });
    }

    const megaWrap = header.querySelector('[data-tpu-mega]');
    if (megaWrap) {
      const megaToggle = megaWrap.querySelector('[data-tpu-mega-toggle]');
      const megaPanel = megaWrap.querySelector('[data-tpu-mega-panel]');
      if (megaToggle && megaPanel) {
        let hoverTimer = null;
        const isOpen = () => megaWrap.classList.contains('is-mega-open');
        const openMega = () => {
          megaWrap.classList.add('is-mega-open');
          megaToggle.setAttribute('aria-expanded', 'true');
          megaPanel.setAttribute('aria-hidden', 'false');
        };
        const closeMega = () => {
          megaWrap.classList.remove('is-mega-open');
          megaToggle.setAttribute('aria-expanded', 'false');
          megaPanel.setAttribute('aria-hidden', 'true');
        };
        const cancelClose = () => {
          if (hoverTimer) {
            window.clearTimeout(hoverTimer);
            hoverTimer = null;
          }
        };
        const scheduleClose = () => {
          cancelClose();
          hoverTimer = window.setTimeout(closeMega, 150);
        };

        [megaToggle, megaPanel].forEach((el) => {
          el.addEventListener('mouseenter', () => {
            cancelClose();
            openMega();
          });
          el.addEventListener('mouseleave', scheduleClose);
        });

        megaToggle.addEventListener('click', () => {
          if (isOpen()) {
            closeMega();
          } else {
            openMega();
          }
        });

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && isOpen()) {
            closeMega();
            megaToggle.focus();
          }
        });

        document.addEventListener('click', (event) => {
          if (!isOpen() || megaWrap.contains(event.target)) return;
          closeMega();
        });
      }
    }

    initUtilityMore(header);

    const initStickyHeader = (header) => {
      const spacer = document.createElement('div');
      spacer.className = 'tpu-header__spacer';
      spacer.setAttribute('aria-hidden', 'true');
      header.parentNode.insertBefore(spacer, header.nextSibling);

      let fixed = false;
      let headerTop = 0;
      let headerHeight = 0;
      let ticking = false;
      let showTimer = null;
      // Wait this long after the header scrolls out of view before sliding
      // the sticky header back in.
      const showDelay = 1000;
      // Re-fix slightly below the trigger point so tiny scrolls near the
      // threshold don't rapidly toggle the slide animation.
      const releaseBuffer = 40;

      // Intentionally NOT gated on prefers-reduced-motion: this is a deliberate,
      // gentle header reveal the merchant explicitly wants.
      const canAnimate = typeof header.animate === 'function';
      let slideAnim = null;

      const clearShowTimer = () => {
        if (showTimer) {
          window.clearTimeout(showTimer);
          showTimer = null;
        }
      };

      const measure = () => {
        if (fixed) return;
        const rect = header.getBoundingClientRect();
        headerTop = rect.top + window.scrollY;
        headerHeight = rect.height;
      };

      const enterFixed = () => {
        if (fixed) return;
        fixed = true;
        spacer.style.height = headerHeight + 'px';
        spacer.style.display = 'block';
        header.classList.add('is-fixed', 'is-stuck');
        publishHeaderHeights();

        if (canAnimate) {
          if (slideAnim) slideAnim.cancel();
          slideAnim = header.animate(
            [{ transform: 'translateY(-100%)' }, { transform: 'translateY(0)' }],
            { duration: 1000, easing: 'ease-in-out', fill: 'backwards' }
          );
        }
      };

      const exitFixed = () => {
        clearShowTimer();
        if (!fixed) return;
        fixed = false;
        if (slideAnim) {
          slideAnim.cancel();
          slideAnim = null;
        }
        spacer.style.display = 'none';
        header.classList.remove('is-fixed', 'is-stuck');
        measure();
        publishHeaderHeights();
      };

      const update = () => {
        const scrollY = window.scrollY;
        if (!fixed) {
          if (scrollY > headerTop + headerHeight) {
            if (!showTimer) {
              showTimer = window.setTimeout(() => {
                showTimer = null;
                if (window.scrollY > headerTop + headerHeight) enterFixed();
              }, showDelay);
            }
          } else {
            clearShowTimer();
          }
        } else if (scrollY <= headerTop + releaseBuffer) {
          exitFixed();
        }
        ticking = false;
      };

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };

      measure();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', () => {
        measure();
        if (fixed) spacer.style.height = headerHeight + 'px';
        publishHeaderHeights();
      }, { passive: true });
      update();
      publishHeaderHeights();
    };

    if (header.classList.contains('is-sticky')) {
      initStickyHeader(header);
    }
  };

  const MEGA_PROMO_STORE = 'tpu-mega-promos';

  const readSourceCards = (prefix) => {
    const grid = document.querySelector('[class*="' + prefix + '"]');
    if (!grid) return null;
    const links = grid.querySelectorAll('a[class*="ai-card-link-"]');
    const cards = [];
    links.forEach((link) => {
      const href = link.getAttribute('href');
      if (!href) return;
      const img = link.querySelector('img');
      const titleEl = link.querySelector('[class*="ai-card-title-"]');
      const subEl = link.querySelector('[class*="ai-card-subtitle-"]');
      cards.push({
        href: href,
        img: img ? img.getAttribute('src') || img.currentSrc || '' : '',
        title: titleEl ? titleEl.textContent.trim() : '',
        subtitle: subEl ? subEl.textContent.trim() : ''
      });
    });
    return cards.length ? cards : null;
  };

  const buildPromo = (card, cta) => {
    const link = document.createElement('a');
    link.className = 'tpu-mega__promo';
    link.href = card.href || '#';
    if (card.img) {
      const img = document.createElement('img');
      img.className = 'tpu-mega__promo-img';
      img.src = card.img;
      img.alt = '';
      img.loading = 'lazy';
      link.appendChild(img);
    }
    const content = document.createElement('span');
    content.className = 'tpu-mega__promo-content';
    if (card.title) {
      const title = document.createElement('span');
      title.className = 'tpu-mega__promo-title';
      title.textContent = card.title;
      content.appendChild(title);
    }
    if (card.subtitle) {
      const text = document.createElement('span');
      text.className = 'tpu-mega__promo-text';
      text.textContent = card.subtitle;
      content.appendChild(text);
    }
    const button = document.createElement('span');
    button.className = 'tpu-mega__promo-btn';
    button.textContent = cta || 'Shop Now';
    content.appendChild(button);
    link.appendChild(content);
    return link;
  };

  const initMegaPromoSync = () => {
    const containers = document.querySelectorAll('[data-tpu-mega-promos]');
    if (!containers.length) return;
    containers.forEach((container) => {
      const prefix = container.getAttribute('data-tpu-promo-source') || 'ai-cards-grid-';
      let cards = readSourceCards(prefix);
      if (cards && storageAvailable) {
        try {
          window.localStorage.setItem(MEGA_PROMO_STORE, JSON.stringify(cards));
        } catch (error) {
          /* ignore */
        }
      }
      if (!cards && storageAvailable) {
        try {
          const cached = window.localStorage.getItem(MEGA_PROMO_STORE);
          if (cached) cards = JSON.parse(cached);
        } catch (error) {
          cards = null;
        }
      }
      if (!cards || !cards.length) return;
      const cta = container.getAttribute('data-tpu-promo-cta') || 'Shop Now';
      const chosen = cards[Math.floor(Math.random() * cards.length)];
      container.textContent = '';
      container.appendChild(buildPromo(chosen, cta));
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-tpu-announcement-bar]').forEach(initAnnouncementBar);
    root.querySelectorAll('[data-tpu-header]').forEach(initHeader);
    initMegaPromoSync();
    requestAnimationFrame(publishHeaderHeights);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('resize', publishHeaderHeights, { passive: true });
})();
